
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import  themen  from 'theme/styles/styled-colors';
import  * as theme  from 'react-syntax-highlighter/dist/esm/styles/hljs';
import SyntaxHighlighter from 'react-syntax-highlighter';


const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <h1 {...{
      "id": "advanced-search"
    }}>{`Advanced Search`}</h1>
    <h3 {...{
      "id": "background"
    }}><strong parentName="h3">{`Background`}</strong></h3>
    <p>{`Advanced Search in Atlas is also referred to as DSL-based Search.`}</p>
    <p>{`Domain Specific Search (DSL) is a language with simple constructs that help users navigate Atlas data repository. The syntax loosely emulates the popular Structured Query Language (SQL) from relation database world.`}</p>
    <p>{`Benefits of DSL:`}</p>
    <ul>
      <li parentName="ul">{`Abstracts the implementation-level database constructs. This avoids the necessity of knowing about the underlying graph database constructs.`}</li>
      <li parentName="ul">{`User are provided with an abstraction that helps them retrieve the data by just being aware of the types and their relationships within their dataset.`}</li>
      <li parentName="ul">{`Allows for a way to specify the desired output.`}</li>
      <li parentName="ul">{`Use of classifications is accounted for in the syntax.`}</li>
      <li parentName="ul">{`Provides way to group and aggregate results.`}</li>
    </ul>
    <p>{`We will be using the quick start dataset in the examples that follow. This dataset is comprehensive enough to be used to to demonstrate the various features of the language.`}</p>
    <p>{`For details on the grammar, please refer to Atlas DSL Grammer on `}<a parentName="p" {...{
        "href": "https://github.com/apache/atlas/blob/master/repository/src/main/java/org/apache/atlas/query/antlr4/AtlasDSLParser.g4"
      }}>{`Github`}</a>{` (Antlr G4 format).`}</p>
    <h2 {...{
      "id": "using-advanced-search"
    }}>{`Using Advanced Search`}</h2>
    <p>{`Within the Atlas UI, select Advanced in the Search pane on the left.`}</p>
    <p>{`Notice that the `}<em parentName="p">{`Favorite Searches`}</em>{` pane below the `}<em parentName="p">{`Search By Query`}</em>{` box. Like `}<em parentName="p">{`Basic Search`}</em>{`, it is possible to save the `}<em parentName="p">{`Advanced Searches`}</em>{` as well.`}</p>
    <h2 {...{
      "id": "introduction-to-domain-specific-language"
    }}>{`Introduction to Domain Specific Language`}</h2>
    <p>{`DSL uses the familiar SQL-like syntax.`}</p>
    <p>{`At a high-level a query has a `}<em parentName="p">{`from-where-select`}</em>{` format. Additional keywords like `}<em parentName="p">{`grouby`}</em>{`, `}<em parentName="p">{`orderby`}</em>{`, `}<em parentName="p">{`limit`}</em>{` can be used to added to affect the output. We will see examples of these below.`}</p>
    <h3 {...{
      "id": "from-clause"
    }}>{`From Clause`}</h3>
    <p>{`Specifying the `}<em parentName="p">{`from`}</em>{` clause is mandatory. Using the `}<em parentName="p">{`from`}</em>{` keyword itself is optional. The value specified in the `}<em parentName="p">{`from`}</em>{` clause acts as the source or starting point for the rest of the query to source its inputs.`}</p>
    <p>{`Example: To retrieve all entities of type `}<em parentName="p">{`DB`}</em>{`:`}</p>
    <SyntaxHighlighter wrapLines={true} language="sql" style={theme.dark} mdxType="SyntaxHighlighter">
      {`DB
from DB`}
    </SyntaxHighlighter>
    <p>{`In the absence of `}<em parentName="p">{`where`}</em>{` for filtering on the source, the dataset fetched by the `}<em parentName="p">{`from`}</em>{` clause is everything from the database. Based on the size of the data present in the database, there is a potential to overwhelm the server. The query processor thus adds `}<em parentName="p">{`limit`}</em>{` clause with a default value set. See the section on `}<em parentName="p">{`limit`}</em>{` clause for details.`}</p>
    <h3 {...{
      "id": "where-clause"
    }}>{`Where Clause`}</h3>
    <p>{`The `}<em parentName="p">{`where`}</em>{` clause allows for filtering over the dataset. This achieved by using conditions within the where clause.`}</p>
    <p>{`A conditions is identifier followed by an operator followed by a literal. Literal must be enclosed in single or double quotes. Example, `}<em parentName="p">{`name = "Sales"`}</em>{`. An identifier can be name of the property of the type specified in the `}<em parentName="p">{`from`}</em>{` clause or an alias.`}</p>
    <p>{`Example: To retrieve entity of type `}<em parentName="p">{`Table`}</em>{` with a specific name say time_dim:`}</p>
    <SyntaxHighlighter wrapLines={true} language="sql" style={theme.dark} mdxType="SyntaxHighlighter">
      {`from Table where name = 'time_dim'`}
    </SyntaxHighlighter>
    <p>{`It is possible to specify multiple conditions by combining them using `}<em parentName="p">{`and`}</em>{`, `}<em parentName="p">{`or`}</em>{` operators.`}</p>
    <p>{`Example: To retrieve entity of type Table with name that can be either time_dim or customer_dim:`}</p>
    <SyntaxHighlighter wrapLines={true} language="sql" style={theme.dark} mdxType="SyntaxHighlighter">
      {`from Table where name = 'time_dim' or name = 'customer_dim'`}
    </SyntaxHighlighter>
    <p>{`Filtering based on a list of values is done using by specifying the values in the square brackets. A value array is a list of values enclosed within square brackets. This is a simple way to specify an OR clause on an identifier.`}</p>
    <p>{`Note that having several OR clauses on the same attribute may be inefficient. Alternate way is to use the value array as shown in the example below.`}</p>
    <p>{`Example: The query in the example above can be written using a value array as shown below.`}</p>
    <SyntaxHighlighter wrapLines={true} language="sql" style={theme.dark} mdxType="SyntaxHighlighter">
      {`from Table where name = ["customer_dim", "time_dim"]`}
    </SyntaxHighlighter>
    <p>{`A condition that uses the LIKE operator, allows for filtering using wildcards like '*' or '?'.
Example: To retrieve entity of type `}<em parentName="p">{`Table`}</em>{` whose name ends with '_dim':`}</p>
    <SyntaxHighlighter wrapLines={true} language="sql" style={theme.dark} mdxType="SyntaxHighlighter">
      {`from Table where name LIKE '*_dim'`}
    </SyntaxHighlighter>
    <p>{`Additional forms of regular expressions can also be used.`}</p>
    <p>{`Example: To retrieve `}<em parentName="p">{`DB`}</em>{` whose name starts with `}<em parentName="p">{`R`}</em>{` followed by has any 3 characters, followed by `}<em parentName="p">{`rt`}</em>{` followed by at least 1 character, followed by none or any number of characters.`}</p>
    <SyntaxHighlighter wrapLines={true} language="html" style={theme.dark} mdxType="SyntaxHighlighter">
      {`DB where name like "R???rt?*"`}
    </SyntaxHighlighter>
    <p>{`Example: To find all the columns in a Table.`}</p>
    <SyntaxHighlighter wrapLines={true} language="html" style={theme.dark} mdxType="SyntaxHighlighter">
      {`Column where table.name="sales_fact"`}
    </SyntaxHighlighter>
    <p>{`Example: To find all the Tables for a column.`}</p>
    <SyntaxHighlighter wrapLines={true} language="html" style={theme.dark} mdxType="SyntaxHighlighter">
      {`Table where columns.name="sales"`}
    </SyntaxHighlighter>
    <p>{`Example: To retrieve all the entities of type `}<em parentName="p">{`Table`}</em>{` that are tagged with `}<em parentName="p">{`Dimension`}</em>{` classification and its attribute `}<em parentName="p">{`priority`}</em>{` having 'high'`}</p>
    <SyntaxHighlighter wrapLines={true} language="sql" style={theme.dark} mdxType="SyntaxHighlighter">
      {`Table where Dimension.priority = "high"`}
    </SyntaxHighlighter>
    <h3 {...{
      "id": "using-date-literals"
    }}>{`Using Date Literals`}</h3>
    <p>{`Dates used in literals need to be specified using the ISO 8601 format.`}</p>
    <p>{`Dates in this format follow this notation:`}</p>
    <ul>
      <li parentName="ul"><em parentName="li">{`yyyy-MM-ddTHH:mm:ss.SSSZ`}</em>{`. Which means, year-month-day followed by time in hour-minutes-seconds-milli-seconds. Date and time need to be separated by 'T'. It should end with 'Z'.`}</li>
      <li parentName="ul"><em parentName="li">{`yyyy-MM-dd`}</em>{`. Which means, year-month-day.`}</li>
    </ul>
    <p>{`Example: Date represents December 11, 2017 at 2:35 AM.`}</p>
    <SyntaxHighlighter wrapLines={true} language="sql" style={theme.dark} mdxType="SyntaxHighlighter">
      {`2017-12-11T02:35:0.0Z`}
    </SyntaxHighlighter>
    <p>{`Example: To retrieve entity of type `}<em parentName="p">{`Table`}</em>{` created within 2017 and 2018.`}</p>
    <SyntaxHighlighter wrapLines={true} language="sql" style={theme.dark} mdxType="SyntaxHighlighter">
      {`from Table where createTime < '2018-01-01' and createTime > '2017-01-01'`}
    </SyntaxHighlighter>
    <h4 {...{
      "id": "using-boolean-literals"
    }}>{`Using Boolean Literals`}</h4>
    <p>{`Properties of entities of type boolean can be used within queries.`}</p>
    <p>{`Eample: To retrieve entity of type hdfs`}<em parentName="p">{`path whose attribute _isFile`}</em>{` is set to `}<em parentName="p">{`true`}</em>{` and whose name is `}<em parentName="p">{`Invoice`}</em>{`.`}</p>
    <SyntaxHighlighter wrapLines={true} language="sql" style={theme.dark} mdxType="SyntaxHighlighter">
      {`from hdfs_path where isFile = true or name = "Invoice"`}
    </SyntaxHighlighter>
    <p>{`Valid values for boolean literals are 'true' and 'false'.`}</p>
    <h3 {...{
      "id": "existence-of-a-property"
    }}>{`Existence of a Property`}</h3>
    <p>{`The has keyword can be used with or without the where clause. It is used to check existence of a property in an entity.`}</p>
    <p>{`Example: To retreive entity of type Table with a property locationUri.`}</p>
    <SyntaxHighlighter wrapLines={true} language="html" style={theme.dark} mdxType="SyntaxHighlighter">
      {`Table has locationUri
from Table where Table has locationUri`}
    </SyntaxHighlighter>
    <h3 {...{
      "id": "select-clause"
    }}>{`Select Clause`}</h3>
    <p>{`If you noticed the output displayed on the web page, it displays a tabular display, each row corresponding to an entity and columns are properties of that entity. The select clause allows for choosing the properties of entity that are of interest.`}</p>
    <p>{`Example: To retrieve entity of type `}<em parentName="p">{`Table`}</em>{` with few properties:`}</p>
    <SyntaxHighlighter wrapLines={true} language="sql" style={theme.dark} mdxType="SyntaxHighlighter">
      {`from Table select owner, name, qualifiedName`}
    </SyntaxHighlighter>
    <p>{`Example: To retrieve entity of type Table for a specific table with some properties.`}</p>
    <SyntaxHighlighter wrapLines={true} language="sql" style={theme.dark} mdxType="SyntaxHighlighter">
      {`from Table where name = 'customer_dim' select owner, name, qualifiedName`}
    </SyntaxHighlighter>
    <p>{`To display column headers that are more meaningful, aliases can be added using the 'as' clause.`}</p>
    <p>{`Example: To display column headers as 'Owner', 'Name' and 'FullName'.`}</p>
    <SyntaxHighlighter wrapLines={true} language="sql" style={theme.dark} mdxType="SyntaxHighlighter">
      {`from Table select owner as Owner, name as Name, qualifiedName as FullName`}
    </SyntaxHighlighter>
    <h4 {...{
      "id": "note-about-select-clauses"
    }}>{`Note About Select Clauses`}</h4>
    <p>{`Given the complexity involved in using select clauses, these are the few rules to remember when using select clauses:`}</p>
    <ul>
      <li parentName="ul">{`Works with all immediate attributes.`}</li>
      <li parentName="ul">{`Works with Immediate attributes and aggregation on immediate attributes.`}</li>
      <li parentName="ul">{`Referred attributes cannot be mixed with immediate attributes.`}</li>
    </ul>
    <p>{`Example: To retrieve entity of type Table with name 'Sales' and display 'name' and 'owner' attribute of the referred entity DB.`}</p>
    <SyntaxHighlighter wrapLines={true} language="sql" style={theme.dark} mdxType="SyntaxHighlighter">
      {`Table where name = 'abcd' select DB.name, DB.owner`}
    </SyntaxHighlighter>
    <p>{`Current implementation does not allow the following:`}</p>
    <SyntaxHighlighter wrapLines={true} language="html" style={theme.dark} mdxType="SyntaxHighlighter">
      {`Table where name = 'abcd' select DB.name, Table.name`}
    </SyntaxHighlighter>
    <h3 {...{
      "id": "classification-based-filtering"
    }}>{`Classification-based Filtering`}</h3>
    <p>{`In order to retrieve entities based on classification, a query would use `}<em parentName="p">{`is`}</em>{` or `}<em parentName="p">{`isa`}</em>{` keywords.`}</p>
    <p>{`Example: To retrieve all entities of type `}<em parentName="p">{`Table`}</em>{` that are tagged with `}<em parentName="p">{`Dimension`}</em>{` classification.`}</p>
    <SyntaxHighlighter wrapLines={true} language="html" style={theme.dark} mdxType="SyntaxHighlighter">
      {`from Table isa Dimension`}
    </SyntaxHighlighter>
    <p>{`Since, from is optional and `}<em parentName="p">{`is`}</em>{` (or `}<em parentName="p">{`isa`}</em>{`) are equivalent, the following queries yield the same results:`}</p>
    <SyntaxHighlighter wrapLines={true} language="html" style={theme.dark} mdxType="SyntaxHighlighter">
      {`Table is Dimension`}
    </SyntaxHighlighter>
    <p>{`The `}<em parentName="p">{`is`}</em>{` and `}<em parentName="p">{`isa`}</em>{` clauses can also be used in `}<em parentName="p">{`where`}</em>{` condition like:`}</p>
    <SyntaxHighlighter wrapLines={true} language="html" style={theme.dark} mdxType="SyntaxHighlighter">
      {`from Table where Table isa Dimension`}
    </SyntaxHighlighter>
    <p>{`To search for all entities having a particular classification, simply use the name of the classification.`}</p>
    <p>{`Example: To retrieve all entities that have `}<em parentName="p">{`Dimension`}</em>{` classification.`}</p>
    <SyntaxHighlighter wrapLines={true} language="sql" style={theme.dark} mdxType="SyntaxHighlighter">
      {`Dimension`}
    </SyntaxHighlighter>
    <p>{`To search for all entities having a particular classification with its attribute, add filter in where clause.`}</p>
    <p>{`Example: To retrieve all the entities that are tagged with `}<em parentName="p">{`Dimension`}</em>{` classification and its attribute `}<em parentName="p">{`priority`}</em>{` having 'high'`}</p>
    <SyntaxHighlighter wrapLines={true} language="sql" style={theme.dark} mdxType="SyntaxHighlighter">
      {`Dimension where Dimension.priority = "high"`}
    </SyntaxHighlighter>
    <p>{`###Non Primitive attribute Filtering
In the discussion so far we looked at where clauses with primitive types. This section will look at using properties that are non-primitive types.`}</p>
    <h4 {...{
      "id": "relationship-based-filtering"
    }}>{`Relationship-based filtering`}</h4>
    <p>{`In this model, the DB is modeled such that it is aware of all the Table it contains. Table on the other hand is aware of existence of the DB but is not aware of all the other `}<em parentName="p">{`Table`}</em>{` instances within the system. Each Table maintains reference of the `}<em parentName="p">{`DB`}</em>{` it belongs to.`}</p>
    <p>{`Similar structure exists within the `}<em parentName="p">{`hive`}</em>{` data model.`}</p>
    <p>{`Example: To retrieve all the instances of the `}<em parentName="p">{`Table`}</em>{` belonging to a database named 'Sales':`}</p>
    <SyntaxHighlighter wrapLines={true} language="html" style={theme.dark} mdxType="SyntaxHighlighter">
      {`Table where db.name = "Sales"`}
    </SyntaxHighlighter>
    <p>{`Example: To retrieve all the instances of the `}<em parentName="p">{`Table`}</em>{` belonging to a database named 'Sales' and whose column name starts with 'customer':`}</p>
    <SyntaxHighlighter wrapLines={true} language="html" style={theme.dark} mdxType="SyntaxHighlighter">
      {`Table where db.name = "Sales" and columns.name like "customer*"`}
    </SyntaxHighlighter>
    <p>{`The entity Column is modeled in a similar way. Each Table entity has outward edges pointing to Column entity instances corresponding to each column within the table.`}</p>
    <p>{`Example: To retrieve all the Column entities for a given Table.`}</p>
    <SyntaxHighlighter wrapLines={true} language="html" style={theme.dark} mdxType="SyntaxHighlighter">
      {`Table where name = "time_dim" select columns`}
    </SyntaxHighlighter>
    <p>{`The properties of each `}<em parentName="p">{`Column`}</em>{` entity type are displayed.`}</p>
    <h4 {...{
      "id": "glossary-term-based-filtering"
    }}>{`Glossary Term-based Filtering`}</h4>
    <p>{`In order to retrieve entities based on glossary term, a query would use `}<em parentName="p">{`hasTerm`}</em>{` keyword.`}</p>
    <p>{`To search for entities having a particular glossary term, user needs to add a fully qualified name. i.e `}<em parentName="p">{`{termName}@{glossaryName}`}</em>{`. In case the user adds only the term name, all the entities with particular term name will be returned, irrespective of which glossary it is in.`}</p>
    <p>{`Example: To retrieve all entities of type `}<em parentName="p">{`Table`}</em>{` having glossary term `}<em parentName="p">{`savingsAccount@Banking`}</em>{`, below are the possible ways.`}</p>
    <SyntaxHighlighter wrapLines={true} language="html" style={theme.dark} mdxType="SyntaxHighlighter">
      {`from Table hasTerm "savingsAccount@Banking"
Table hasTerm "savingsAccount@Banking"
Table hasTerm "savingsAccount"
Table where Table hasTerm "savingsAccount@Banking"`}
    </SyntaxHighlighter>
    <p>{`Example: To retrieve all entities of type `}<em parentName="p">{`Table`}</em>{` having glossary term `}<em parentName="p">{`savingsAccount@Banking`}</em>{` and whose name is 'customer'.`}</p>
    <SyntaxHighlighter wrapLines={true} language="html" style={theme.dark} mdxType="SyntaxHighlighter">
      {`from Table hasTerm "savingsAccount@Banking" and name = "customer"`}
    </SyntaxHighlighter>
    <p>{`Example: To retrieve all entities of type `}<em parentName="p">{`Table`}</em>{` having glossary term `}<em parentName="p">{`savingsAccount@Banking`}</em>{` or tagged with 'Dimension' classification and whose column name starts with 'customer'.`}</p>
    <SyntaxHighlighter wrapLines={true} language="html" style={theme.dark} mdxType="SyntaxHighlighter">
      {`from Table hasTerm "savingsAccount@Banking" or Table isA Dimension and (columns.name like "customer*")`}
    </SyntaxHighlighter>
    <h3 {...{
      "id": "limit--offset-clauses"
    }}>{`Limit & Offset Clauses`}</h3>
    <p>{`Often a query yields large number of results. To limit the outcome of the query, the limit and offset clauses are used.`}</p>
    <p>{`Example: To retrieve only the 5 entities from a result set.`}</p>
    <SyntaxHighlighter wrapLines={true} language="sql" style={theme.dark} mdxType="SyntaxHighlighter">
      {`Column limit 5`}
    </SyntaxHighlighter>
    <p>{`The offset clauses retrieves results after the offset value.`}</p>
    <p>{`Example: To retrieve only 5 entities from the result set after skipping the first 10.`}</p>
    <SyntaxHighlighter wrapLines={true} language="sql" style={theme.dark} mdxType="SyntaxHighlighter">
      {`Column limit 5 offset 10`}
    </SyntaxHighlighter>
    <p>{`The `}<em parentName="p">{`limit`}</em>{` and `}<em parentName="p">{`offset`}</em>{` clauses are usually specified in conjunction.`}</p>
    <p>{`If no limit clause is specified in the query, a limit clause with a default limit (usually 100) is added to the query. This prevents the query from inadvertently fetching large number of results.`}</p>
    <p>{`The `}<em parentName="p">{`offset`}</em>{` clause is useful for displaying results in a user interface where few results from the result set are showing and more results are fetched as the user advances to next page.`}</p>
    <h3 {...{
      "id": "ordering-results"
    }}>{`Ordering Results`}</h3>
    <p>{`The `}<em parentName="p">{`orderby`}</em>{` clause allows for sorting of results. Results are sorted in ascending order by default. Only immediate attributes can be used within this clause.`}</p>
    <p>{`Ordering can be changed by using:`}</p>
    <ul>
      <li parentName="ul">{`ASC Sort in ascending order. This is the default. If no ordering is specified after the `}<em parentName="li">{`orderby`}</em>{` clause.`}</li>
      <li parentName="ul">{`DESC Sort in descending order. This needs to be explicitly specified after the `}<em parentName="li">{`orderby`}</em>{` clause.`}</li>
    </ul>
    <p>{`Example: To retrieve the entities of type `}<em parentName="p">{`Column`}</em>{` that are sorted in ascending order using the name property.`}</p>
    <SyntaxHighlighter wrapLines={true} language="html" style={theme.dark} mdxType="SyntaxHighlighter">
      {`from Column orderby name
from Column orderby name asc`}
    </SyntaxHighlighter>
    <p>{`Example: Same results as above except that they are sorted in descending order.`}</p>
    <SyntaxHighlighter wrapLines={true} language="html" style={theme.dark} mdxType="SyntaxHighlighter">
      {`from Column orderby name desc`}
    </SyntaxHighlighter>
    <p>{`Example: To retrieve the entities of type `}<em parentName="p">{`Column`}</em>{` filtered with name and associated with 'savingsAccount@Banking' glossary term, that are sorted in ascending order using the name property.`}</p>
    <SyntaxHighlighter wrapLines={true} language="html" style={theme.dark} mdxType="SyntaxHighlighter">
      {`from Column hasTerm "savingsAccount@Banking" and name = "customer_id" orderby name asc`}
    </SyntaxHighlighter>
    <h3 {...{
      "id": "aggregate-functions"
    }}>{`Aggregate Functions`}</h3>
    <p>{`Let's look at aggregate functions:`}</p>
    <ul>
      <li parentName="ul"><em parentName="li">{`sum`}</em>{`: Adds (sums up) a value of the property specified, within the result set.`}</li>
      <li parentName="ul"><em parentName="li">{`min`}</em>{`: Finds the minimum value of the property specified, within a result set.`}</li>
      <li parentName="ul"><em parentName="li">{`max`}</em>{`: Finds the maximum value of the property specified, within a result set.`}</li>
      <li parentName="ul"><em parentName="li">{`count`}</em>{`: Finds the number of items specified by the group by clause.`}</li>
    </ul>
    <p>{`These work only on immediate attributes.`}</p>
    <p>{`Other examples of these in the `}<em parentName="p">{`Grouping Results`}</em>{` section.`}</p>
    <h3 {...{
      "id": "the-count-keyword"
    }}>{`The count Keyword`}</h3>
    <p>{`Shows the number of items in a result set.`}</p>
    <p>{`Example: To know how may entities of a type Column.`}</p>
    <SyntaxHighlighter wrapLines={true} language="html" style={theme.dark} mdxType="SyntaxHighlighter">
      {`Column select count()`}
    </SyntaxHighlighter>
Example: Same as above with alias.
    <SyntaxHighlighter wrapLines={true} language="html" style={theme.dark} mdxType="SyntaxHighlighter">
      {`Column select count() as Cols`}
    </SyntaxHighlighter>
    <p>{`Example: To find the number of tables in a database.`}</p>
    <SyntaxHighlighter wrapLines={true} language="html" style={theme.dark} mdxType="SyntaxHighlighter">
      {`Table where db.name = "Reporting" select count()`}
    </SyntaxHighlighter>
    <p>{`Example: To find the number of terms associated with particular type 'Table'.`}</p>
    <SyntaxHighlighter wrapLines={true} language="html" style={theme.dark} mdxType="SyntaxHighlighter">
      {`Table hasTerm "savingsAccount@Banking" select count() as terms`}
    </SyntaxHighlighter>
    <h3 {...{
      "id": "the-max-keyword"
    }}>{`The max Keyword`}</h3>
    <p>{`Using this keyword it is possible to retrieve the maximum value of a property for an entity.`}</p>
    <p>{`Example: Get the most recently created value of the `}<em parentName="p">{`createTime`}</em>{` property of the Table entity.`}</p>
    <SyntaxHighlighter wrapLines={true} language="html" style={theme.dark} mdxType="SyntaxHighlighter">
      {`Table select max(createTime)`}
    </SyntaxHighlighter>
    <h3 {...{
      "id": "the-min-keyword"
    }}>{`The min Keyword`}</h3>
    <p>{`Using this keyword it is possible to retrieve the minimum value of a property for an entity.`}</p>
    <p>{`Example: Get the least recently created value of the `}<em parentName="p">{`createTime`}</em>{` property of the Table entity.`}</p>
    <SyntaxHighlighter wrapLines={true} language="html" style={theme.dark} mdxType="SyntaxHighlighter">
      {`Table select min(createTime)`}
    </SyntaxHighlighter>
    <h3 {...{
      "id": "grouping-results"
    }}>{`Grouping Results`}</h3>
    <p>{`The `}<em parentName="p">{`groupby`}</em>{` clause groups results within the result using specified property.`}</p>
    <p>{`Example: To retrieve entity of type Table such that tables belonging to an owner are together (grouped by owner).`}</p>
    <SyntaxHighlighter wrapLines={true} language="html" style={theme.dark} mdxType="SyntaxHighlighter">
      {`Table groupby(owner)`}
    </SyntaxHighlighter>
    <p>{`While `}<em parentName="p">{`groupby`}</em>{` can work without `}<em parentName="p">{`select`}</em>{`, if aggregate functions are used within `}<em parentName="p">{`select`}</em>{` clause, using `}<em parentName="p">{`groupby`}</em>{` clause becomes mandatory as aggregate functions operate on a group.`}</p>
    <p>{`Example: To retrieve entity of type Table such we know the most recently created entity.`}</p>
    <SyntaxHighlighter wrapLines={true} language="html" style={theme.dark} mdxType="SyntaxHighlighter">
      {`Table groupby(createTime) select owner, name, max(createTime)`}
    </SyntaxHighlighter>
    <p>{`Example: To retrieve entity of type Table such we know the oldest entity.`}</p>
    <SyntaxHighlighter wrapLines={true} language="html" style={theme.dark} mdxType="SyntaxHighlighter">
      {`Table groupby(createTime) select owner, name, min(createTime)`}
    </SyntaxHighlighter>
    <p>{`Example: To know the number of entities owned by each owner.`}</p>
    <SyntaxHighlighter wrapLines={true} language="html" style={theme.dark} mdxType="SyntaxHighlighter">
      {`Table groupby(owner) select owner, count()`}
    </SyntaxHighlighter>
    <h3 {...{
      "id": "using-system-attributes"
    }}>{`Using System Attributes`}</h3>
    <p>{`Each type defined within Atlas gets few attributes by default. These attributes help with internal book keeping of the entities. All the system attributes are prefixed with '__' (double underscore). This helps in identifying them from other attributes.
Following are the system attributes:`}</p>
    <ul>
      <li parentName="ul">{`__guid Each entity within Atlas is assigned a globally unique identifier (GUID for short).`}</li>
      <li parentName="ul">{`__modifiedBy Name of the user who last modified the entity.`}</li>
      <li parentName="ul">{`__createdBy Name of the user who created the entity.`}</li>
      <li parentName="ul">{`__state Current state of the entity. Please see below for details.`}</li>
      <li parentName="ul">{`__timestamp Timestamp (date represented as integer) of the entity at the time of creation.`}</li>
      <li parentName="ul">{`__modificationTimestamp Timestamp (date represented as integer) of the entity at the time of last modification.`}</li>
    </ul>
    <h3 {...{
      "id": "state-of-an-entity"
    }}>{`State of an Entity`}</h3>
    <p>{`Entity within Atlas can be in the following states:`}</p>
    <ul>
      <li parentName="ul">{`ACTIVE This is the state of entities that when it is available and is used within the system. It can be retrieved by default by searches.`}</li>
      <li parentName="ul">{`DELETED When an entity is deleted, it's state is marked as DELETED. Entity in this state does not show up in search results. Explicit request needs to be made to retrieve this entity.`}</li>
    </ul>
    <h3 {...{
      "id": "using-system-attributes-in-queries"
    }}>{`Using System Attributes in Queries`}</h3>
    <p>{`Example: To retrieve all entities that are deleted.`}</p>
    <SyntaxHighlighter wrapLines={true} language="html" style={theme.dark} mdxType="SyntaxHighlighter">
      {`Asset where __state = "DELETED"`}
    </SyntaxHighlighter>
    <p>{`Example: To retrieve entity GUIDs.`}</p>
    <SyntaxHighlighter wrapLines={true} language="html" style={theme.dark} mdxType="SyntaxHighlighter">
      {`Table select __guid`}
    </SyntaxHighlighter>
    <p>{`Example: To retrieve several system attributes.`}</p>
    <SyntaxHighlighter wrapLines={true} language="html" style={theme.dark} mdxType="SyntaxHighlighter">
      {`hive_db select __timestamp, __modificationTimestamp, __state, __createdBy`}
    </SyntaxHighlighter>
    <h2 {...{
      "id": "advanced-search-rest-api"
    }}>{`Advanced Search REST API`}</h2>
    <p>{`Relevant models for these operations:`}</p>
    <ul>
      <li parentName="ul"><em parentName="li"><a parentName="em" {...{
            "href": "https://github.com/apache/atlas/blob/master/intg/src/main/java/org/apache/atlas/model/discovery/AtlasSearchResult.java"
          }}>{`AtlasSearchResult`}</a></em></li>
      <li parentName="ul"><em parentName="li"><a parentName="em" {...{
            "href": "https://github.com/apache/atlas/blob/master/intg/src/main/java/org/apache/atlas/exception/AtlasBaseException.java"
          }}>{`AtlasBaseException`}</a></em></li>
    </ul>
    <h3 {...{
      "id": "the-v2-api"
    }}>{`The V2 API`}</h3>
    <p><strong parentName="p">{`Get Results using DSL Search`}</strong></p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": "center"
          }}><strong parentName="th"><em parentName="strong">{`Example`}</em></strong></th>
          <th parentName="tr" {...{
            "align": "center"
          }}><strong parentName="th">{`See Examples sections below.`}</strong></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "center"
          }}><em parentName="td">{`URL`}</em></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><em parentName="td">{`api/atlas/v2/search/dsl`}</em></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "center"
          }}><em parentName="td">{`Method`}</em></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><em parentName="td">{`GET`}</em></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "center"
          }}><em parentName="td">{`URL Parameters`}</em></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><em parentName="td">{`query`}</em>{`: Query conforming to DSL syntax.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "center"
          }}></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><em parentName="td">{`typeName`}</em>{`: Type name of the entity to be retrived.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "center"
          }}></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><em parentName="td">{`classification`}</em>{`: Classification associated with the type or query.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "center"
          }}></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><em parentName="td">{`limit`}</em>{`: Maximum number of items in the result set.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "center"
          }}></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><em parentName="td">{`offset`}</em>{`: Starting index of the item in the result set.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "center"
          }}><em parentName="td">{`Data Parameters`}</em></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><em parentName="td">{`None`}</em></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "center"
          }}><em parentName="td">{`Success Response`}</em></td>
          <td parentName="tr" {...{
            "align": "center"
          }}>{`The JSON will correspond to `}<a parentName="td" {...{
              "href": "https://github.com/apache/atlas/blob/master/intg/src/main/java/org/apache/atlas/model/discovery/AtlasSearchResult.java"
            }}>{`AtlasSearchResult`}</a>{`.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "center"
          }}><em parentName="td">{`Error Response`}</em></td>
          <td parentName="tr" {...{
            "align": "center"
          }}>{`Errors that are handled within the system will be returned as `}<a parentName="td" {...{
              "href": "https://github.com/apache/atlas/blob/master/intg/src/main/java/org/apache/atlas/exception/AtlasBaseException.java"
            }}>{`AtlasBaseException`}</a>{`.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "center"
          }}><em parentName="td">{`Method Signature`}</em></td>
          <td parentName="tr" {...{
            "align": "center"
          }}>{`@GET`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "center"
          }}></td>
          <td parentName="tr" {...{
            "align": "center"
          }}>{`@Path("/dsl")`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "center"
          }}></td>
          <td parentName="tr" {...{
            "align": "center"
          }}>{`@Consumes(Servlets.JSON_MEDIA_TYPE)`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "center"
          }}></td>
          <td parentName="tr" {...{
            "align": "center"
          }}>{`@Produces(Servlets.JSON_MEDIA_TYPE)`}</td>
        </tr>
      </tbody>
    </table>
    <p><em parentName="p">{`Examples`}</em></p>
    <SyntaxHighlighter wrapLines={true} language="html" style={theme.dark} mdxType="SyntaxHighlighter">
      {`curl -X GET -u admin:admin -H "Content-Type: application/json" "http://localhost:21000/api/atlas/v2/search/dsl?typeName=Table"
curl -X GET -u admin:admin -H "Content-Type: application/json" "http://localhost:21000/api/atlas/v2/search/dsl?typeName=Column&classification=PII"
curl -X GET -u admin:admin -H "Content-Type: application/json" "http://localhost:21000/api/atlas/v2/search/dsl?typeName=Table&classification=Dimension&limit=10&offset=2"
curl -X GET -u admin:admin -H "Content-Type: application/json" "http://localhost:21000/api/atlas/v2/search/dsl?query=Table%20isa%20Dimension"
curl -X GET -u admin:admin -H "Content-Type: application/json" "http://localhost:21000/api/atlas/v2/search/dsl?query=Table%20isa%20Dimension&limit=5&offset=2"`}
    </SyntaxHighlighter>
    <h2 {...{
      "id": "implementation-approach"
    }}>{`Implementation Approach`}</h2>
    <p>{`The general approach followed in implementation of DSL within Atlas can be enumerated in following steps:`}</p>
    <ul>
      <li parentName="ul">{`Parser parses the incoming query for syntax.`}</li>
      <li parentName="ul">{`Abstract syntax tree is generated by for a query that is parsed successfully.`}</li>
      <li parentName="ul">{`Syntax tree is 'walked' using visitor pattern.`}</li>
      <li parentName="ul">{`Each 'visit' within the tree adds a step in the Gremlin pipeline.`}</li>
      <li parentName="ul">{`When done, the generated script is executed using Gremlin Script Engine.`}</li>
      <li parentName="ul">{`Results generated be the query, if any, are processed and packaged in AtlasSearchResult structure.`}</li>
    </ul>
    <h2 {...{
      "id": "differences-between-master-and-earlier-versions"
    }}>{`Differences Between Master and Earlier Versions`}</h2>
    <p>{`The following clauses are no longer supported:`}</p>
    <ul>
      <li parentName="ul">{`path`}</li>
      <li parentName="ul">{`loop`}</li>
    </ul>
    <h2 {...{
      "id": "resources"
    }}>{`Resources`}</h2>
    <ul>
      <li parentName="ul">{`Antlr `}<a parentName="li" {...{
          "href": "https://pragprog.com/book/tpantlr2/the-definitive-antlr-4-reference"
        }}>{`Book`}</a>{`.`}</li>
      <li parentName="ul">{`Antlr `}<a parentName="li" {...{
          "href": "https://github.com/antlr/antlr4/blob/master/doc/getting-started.md"
        }}>{`Quick Start`}</a>{`.`}</li>
      <li parentName="ul">{`Atlas DSL Grammar on `}<a parentName="li" {...{
          "href": "https://github.com/apache/atlas/blob/master/repository/src/main/java/org/apache/atlas/query/antlr4/AtlasDSLParser.g4"
        }}>{`Github`}</a>{` (Antlr G4 format).`}</li>
    </ul>
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;